/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { EuiButtonIcon, EuiContextMenu, EuiPopover, useGeneratedHtmlId } from '@elastic/eui'

import { withErrorBoundary } from '@modules/cui/Boundary'
import { useToggle } from '@modules/utils/hooks/useToggle'

import ChangeBillingAccountFlyout from '@/components/Organization/OrganizationOverview/MarketplaceSettingsButton/ChangeBillingAccountFlyout'

import { messages } from './messages'

import type { MarketplaceSettingsButtonProps } from './types'
import type { FunctionComponent } from 'react'

const MarketplaceSettingsButton: FunctionComponent<MarketplaceSettingsButtonProps> = ({
  saasOrganization,
}) => {
  const [isPopoverOpen, togglePopover, setPopoverOpen] = useToggle(false)

  const [isBillingAccountChangeFlyoutOpen, setBillingAccountChangeFlyout] = useState(false)

  const settingsMenuPopoverId = useGeneratedHtmlId({
    prefix: 'settingsMenuPopover',
  })

  const onButtonClick = togglePopover

  const closePopover = () => {
    setPopoverOpen(false)
  }

  const onBillingAccountChangeClick = () => {
    setBillingAccountChangeFlyout(true)
    closePopover()
  }

  const closeBillingAccountChangeFlyout = () => {
    setBillingAccountChangeFlyout(false)
  }

  const renderChangeBillingAccountFlyout = () => (
    <ChangeBillingAccountFlyout
      saasOrganization={saasOrganization}
      onClose={closeBillingAccountChangeFlyout}
    />
  )

  const button = (
    <EuiButtonIcon
      iconType={'gear'}
      aria-label='marketplaceSettingsButton'
      onClick={onButtonClick}
      data-test-id='settings-button'
    />
  )

  const gcpPanelItems = [
    {
      name: <FormattedMessage {...messages.changeBillingAccount} />,
      icon: 'documentEdit',
      onClick: onBillingAccountChangeClick,
      data: 'change-billing-account',
    },
  ]

  let panelItems: any[] = []

  switch (saasOrganization.domain) {
    case 'gcp':
      panelItems = gcpPanelItems
      break

    default:
      panelItems = [
        {
          name: <FormattedMessage {...messages.settingsNotAvailable} />,
          icon: 'warning',
          onClick: closePopover,
        },
      ]
  }

  const panels = [
    {
      id: 0,
      title: <FormattedMessage {...messages.settingsTitle} />,
      items: panelItems,
    },
  ]

  return (
    <Fragment>
      <EuiPopover
        id={settingsMenuPopoverId}
        button={button}
        isOpen={isPopoverOpen}
        closePopover={closePopover}
        panelPaddingSize='none'
        anchorPosition='downLeft'
        data-test-id='settings-popover'
      >
        <EuiContextMenu initialPanelId={0} panels={panels} />
      </EuiPopover>
      {isBillingAccountChangeFlyoutOpen && renderChangeBillingAccountFlyout()}
    </Fragment>
  )
}

export default withErrorBoundary(MarketplaceSettingsButton)
